import React from "react";
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  Chip,
  Button,
  ClickAwayListener,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon, Add as AddIcon } from "@mui/icons-material";
import SectionCard from "./SectionCard";
import SuggestionPopper from "./SuggestionPopper";
import {
  BG_ALT,
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  BLUE_PALE,
  BORDER,
  BORDER_MED,
  INK,
  INK_MUTED,
  INK_SOFT,
  SUCCESS,
  SUCCESS_BG,
  fieldSx,
  getFieldSx,
  mobileFieldProps,
} from "./shared";

// Fields that carry a customer-search suggestion dropdown.
const SEARCH_FIELDS = ["name", "mobile", "guardian", "aadhar", "pan"];

// Shared geometry for the two header action pills (Lookup / Create).
const pillSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.4,
  fontSize: "0.7rem",
  fontWeight: 700,
  px: 1.25,
  py: 0.4,
  borderRadius: "6px",
  letterSpacing: 0.5,
  whiteSpace: "nowrap",
  transition: "all 0.15s",
};

// ─── Customer Details section (step 1) ───────────────────────────────────────
// Presentational block extracted verbatim from PaymentScreen. State and
// handlers stay in the parent and arrive here as props.
const CustomerSection = ({
  stepStateOf,
  openSections,
  toggleSection,
  sectionSummary,
  paymentLocked,
  isMobile,
  setLookupOpen,
  setDialogExistingCustomerId,
  setDialogExistingCustomerName,
  setNewCustomerOpen,
  formData,
  setFormData,
  setNomineesList,
  errors,
  nameInputRef,
  mobileInputRef,
  handleSearchableFieldChange,
  handleFieldFocus,
  closeSuggestions,
  suggestionLoading,
  activeField,
  suggestions,
  selectCustomer,
}) => {
  // Anchor elements for the suggestion dropdowns. These are tracked with ref
  // callbacks (not captured on focus) because the section content is unmounted
  // whenever the accordion collapses — a node captured earlier would be
  // detached by the time the dropdown opens, and a detached anchor makes
  // Popper fall back to the top-left corner of the viewport.
  const [anchors, setAnchors] = React.useState({});
  const anchorRefs = React.useMemo(
    () =>
      Object.fromEntries(
        SEARCH_FIELDS.map((field) => [
          field,
          (el) =>
            setAnchors((prev) =>
              prev[field] === el ? prev : { ...prev, [field]: el },
            ),
        ]),
      ),
    [],
  );

  return (
                <SectionCard
                  icon="👤"
                  title="Customer Details"
                  step={1}
                  stepState={stepStateOf(1)}
                  collapsed={!openSections[1]}
                  onToggle={() => toggleSection(1)}
                  summary={sectionSummary[1]}
                  readOnly={paymentLocked}
                  action={
                    paymentLocked ? undefined : isMobile ? (
                      // Mobile: icon buttons
                      <Box sx={{ display: "flex", gap: 0.75 }}>
                        <Tooltip title="Search Customer">
                          <IconButton
                            size="small"
                            onClick={() => setLookupOpen(true)}
                            sx={{
                              bgcolor: BLUE,
                              color: "#fff",
                              borderRadius: "8px",
                              width: 32,
                              height: 32,
                              "&:hover": { bgcolor: BLUE_DARK },
                            }}
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="New Customer">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDialogExistingCustomerId(null);
                              setDialogExistingCustomerName("");
                              setNewCustomerOpen(true);
                            }}
                            sx={{
                              bgcolor: BG_ALT,
                              color: INK,
                              border: `1px solid ${BORDER}`,
                              borderRadius: "8px",
                              width: 32,
                              height: 32,
                              "&:hover": {
                                bgcolor: BLUE_PALE,
                                borderColor: BLUE,
                              },
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      // Desktop: a matching pair of pills — lookup (F1) and
                      // new customer (F2), the two shortcuts the screen
                      // already binds on the keyboard.
                      <Box sx={{ display: "flex", gap: 0.75 }}>
                        <Tooltip title="Open customer lookup (F1)">
                          <Box
                            onClick={() =>
                              !paymentLocked && setLookupOpen(true)
                            }
                            sx={{
                              ...pillSx,
                              bgcolor: paymentLocked ? BORDER_MED : BLUE,
                              color: "#fff",
                              cursor: paymentLocked ? "not-allowed" : "pointer",
                              "&:hover": {
                                bgcolor: paymentLocked ? BORDER_MED : BLUE_DARK,
                              },
                            }}
                          >
                            <SearchIcon sx={{ fontSize: 14 }} />
                            Lookup
                          </Box>
                        </Tooltip>
                        <Tooltip title="Create a customer (F2)">
                          <Box
                            onClick={() => {
                              if (paymentLocked) return;
                              setDialogExistingCustomerId(null);
                              setDialogExistingCustomerName("");
                              setNewCustomerOpen(true);
                            }}
                            sx={{
                              ...pillSx,
                              bgcolor: BG_ALT,
                              color: paymentLocked ? INK_MUTED : INK,
                              border: `1px solid ${BORDER}`,
                              cursor: paymentLocked ? "not-allowed" : "pointer",
                              "&:hover": paymentLocked
                                ? undefined
                                : { bgcolor: BLUE_PALE, borderColor: BLUE },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 14 }} />
                            Create
                          </Box>
                        </Tooltip>
                      </Box>
                    )
                  }
                >
                  {formData.customerId ? (
                    /* ── Selected-customer summary card (compact strip) ──── */
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 0.75,
                        pl: 1,
                        bgcolor: BLUE_PALE,
                        border: `1px solid ${BLUE_LIGHT}`,
                        borderRadius: "8px",
                        flexWrap: "wrap",
                        gap: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            bgcolor: BLUE,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 800,
                            fontSize: "0.85rem",
                            flexShrink: 0,
                          }}
                        >
                          {formData.customerName?.[0]?.toUpperCase() || "C"}
                        </Box>
                        {/* Name, contact and doc chips on a single wrapping line
                          so the loaded-customer strip stays one row tall. */}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            columnGap: 1,
                            rowGap: 0.25,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            fontWeight={700}
                            sx={{ color: INK, fontSize: "0.85rem" }}
                          >
                            {formData.customerName}
                          </Typography>
                          {formData.mobile && (
                            <Typography
                              variant="caption"
                              sx={{ color: INK_SOFT }}
                            >
                              📞 {formData.mobile}
                            </Typography>
                          )}
                          {formData.address && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: INK_MUTED,
                                fontSize: "0.72rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 260,
                              }}
                            >
                              📍 {formData.address}
                            </Typography>
                          )}
                          {formData.aadhar && (
                            <Chip
                              label="Aadhaar ✓"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.62rem",
                                bgcolor: SUCCESS_BG,
                                color: SUCCESS,
                                border: `1px solid ${SUCCESS}40`,
                              }}
                            />
                          )}
                          {formData.pan && (
                            <Chip
                              label="PAN ✓"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.62rem",
                                bgcolor: SUCCESS_BG,
                                color: SUCCESS,
                                border: `1px solid ${SUCCESS}40`,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setFormData((p) => ({
                            ...p,
                            customerId: "",
                            customerName: "",
                            mobile: "",
                            phone: "",
                            aadhar: "",
                            pan: "",
                            sonOf: "",
                            address: "",
                          }));
                          setNomineesList([]);
                        }}
                        sx={{
                          borderColor: BORDER_MED,
                          color: INK_SOFT,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.4,
                          "&:hover": {
                            borderColor: BLUE,
                            color: BLUE,
                            bgcolor: BLUE_PALE,
                          },
                        }}
                      >
                        ✎ Change
                      </Button>
                    </Box>
                  ) : (
                    /* ── Customer search fields ──────────────────────────── */
                    <ClickAwayListener onClickAway={closeSuggestions}>
                      <div>
                        <Grid container spacing={1.5}>
                          <Grid item md={2.4} sm={6} xs={12}>
                            <TextField
                              fullWidth
                              ref={anchorRefs.name}
                              inputRef={nameInputRef}
                              size="small"
                              label="Customer Name *"
                              value={formData.customerName}
                              placeholder="Type to search"
                              onChange={(e) =>
                                handleSearchableFieldChange(
                                  "name",
                                  e.target.value,
                                  "customerName",
                                )
                              }
                              onFocus={(e) => handleFieldFocus("name", e)}
                              error={!!errors.customerName}
                              helperText={errors.customerName || ""}
                              // sx={fieldSx}
                              sx={getFieldSx(!!errors.customerName)}
                              InputProps={{
                                endAdornment: suggestionLoading.name ? (
                                  <InputAdornment position="end">
                                    <CircularProgress
                                      size={14}
                                      sx={{ color: BLUE }}
                                    />
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          </Grid>
                          <Grid item md={1.8} sm={6} xs={12}>
                            <TextField
                              fullWidth
                              ref={anchorRefs.mobile}
                              inputRef={mobileInputRef}
                              size="small"
                              label="Mobile"
                              {...mobileFieldProps()}
                              value={formData.mobile}
                              onChange={(e) =>
                                handleSearchableFieldChange(
                                  "mobile",
                                  e.target.value,
                                  "mobile",
                                )
                              }
                              onFocus={(e) => handleFieldFocus("mobile", e)}
                              sx={fieldSx}
                              InputProps={{
                                endAdornment: suggestionLoading.mobile ? (
                                  <InputAdornment position="end">
                                    <CircularProgress
                                      size={14}
                                      sx={{ color: BLUE }}
                                    />
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          </Grid>
                          <Grid item md={1.8} sm={6} xs={6}>
                            <TextField
                              fullWidth
                              ref={anchorRefs.aadhar}
                              size="small"
                              label="Aadhar"
                              value={formData.aadhar}
                              onChange={(e) =>
                                handleSearchableFieldChange(
                                  "aadhar",
                                  e.target.value,
                                  "aadhar",
                                )
                              }
                              onFocus={(e) => handleFieldFocus("aadhar", e)}
                              sx={fieldSx}
                              InputProps={{
                                endAdornment: suggestionLoading.aadhar ? (
                                  <InputAdornment position="end">
                                    <CircularProgress
                                      size={14}
                                      sx={{ color: BLUE }}
                                    />
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          </Grid>
                          <Grid item md={1.5} sm={6} xs={6}>
                            <TextField
                              fullWidth
                              ref={anchorRefs.pan}
                              size="small"
                              label="PAN"
                              value={formData.pan}
                              onChange={(e) =>
                                handleSearchableFieldChange(
                                  "pan",
                                  e.target.value,
                                  "pan",
                                )
                              }
                              onFocus={(e) => handleFieldFocus("pan", e)}
                              sx={fieldSx}
                              InputProps={{
                                endAdornment: suggestionLoading.pan ? (
                                  <InputAdornment position="end">
                                    <CircularProgress
                                      size={14}
                                      sx={{ color: BLUE }}
                                    />
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          </Grid>
                          <Grid item md={2.1} sm={6} xs={12}>
                            <TextField
                              fullWidth
                              ref={anchorRefs.guardian}
                              size="small"
                              label="S/W/D of"
                              value={formData.sonOf}
                              placeholder="Son / Wife / Daughter of"
                              onChange={(e) =>
                                handleSearchableFieldChange(
                                  "guardian",
                                  e.target.value,
                                  "sonOf",
                                )
                              }
                              onFocus={(e) => handleFieldFocus("guardian", e)}
                              sx={fieldSx}
                              InputProps={{
                                endAdornment: suggestionLoading.guardian ? (
                                  <InputAdornment position="end">
                                    <CircularProgress
                                      size={14}
                                      sx={{ color: BLUE }}
                                    />
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          </Grid>
                          <Grid item md={2.4} sm={6} xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Address"
                              value={formData.address}
                              multiline
                              rows={isMobile ? 1 : 2}
                              sx={{
                                ...fieldSx,
                                "& .MuiInputBase-input": {
                                  color: INK_MUTED,
                                  fontSize: "0.8rem",
                                },
                              }}
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                        </Grid>

                        {SEARCH_FIELDS.map((field) => (
                          <SuggestionPopper
                            key={field}
                            open={activeField === field}
                            anchorEl={anchors[field]}
                            customers={suggestions[field]}
                            loading={suggestionLoading[field]}
                            onSelect={selectCustomer}
                          />
                        ))}
                      </div>
                    </ClickAwayListener>
                  )}
                </SectionCard>
  );
};

export default CustomerSection;
